import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { intlLocaleFor } from "@/lib/appLocale";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface CriticalAlert {
  id: string;
  location_id: string;
  location_address: string;
  section_name: string | null;
  scheduled_at: string;
  coordinator_notes: string | null;
}

interface AdminDashboardAlertsProps {
  alerts: CriticalAlert[];
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  className?: string;
}

export function AdminDashboardAlerts({
  alerts,
  hasMore,
  onLoadMore,
  isLoadingMore,
  className,
}: AdminDashboardAlertsProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = intlLocaleFor(i18n.language);

  if (alerts.length === 0) {
    return (
      <div
        className={cn(
          "rounded-lg border border-border bg-card p-4 text-center text-sm text-muted-foreground",
          className
        )}
      >
        {t("admin.alerts.none")}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-lg border border-destructive/50 bg-destructive/5 p-4",
        className
      )}
    >
      <h3 className="flex items-center gap-2 text-sm font-semibold text-destructive mb-3">
        <AlertTriangle className="h-4 w-4" />
        {t("admin.alerts.title")}
      </h3>
      <ul className="space-y-2">
        {alerts.map((a) => (
          <li key={a.id}>
            <Link
              to={`/properties/${a.location_id}`}
              className="block rounded border border-border bg-background p-2 text-sm hover:bg-accent transition-colors"
            >
              <span className="font-medium">{a.location_address}</span>
              {a.section_name && (
                <span className="text-muted-foreground"> · {a.section_name}</span>
              )}
              <span className="text-muted-foreground block text-xs mt-0.5">
                {t("admin.alerts.due", { date: new Date(a.scheduled_at).toLocaleDateString(dateLocale) })}
                {a.coordinator_notes && ` · ${a.coordinator_notes}`}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {hasMore && onLoadMore && (
        <div className="flex justify-center pt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            {t("admin.alerts.loadOlder")}
          </Button>
        </div>
      )}
    </div>
  );
}
